/**
 * Process-wide accounting of decoded audio buffer memory across every
 * synth/sampler. Used to enforce `PerfProfile.audio.sampleBudgetMB` so the
 * `lazy` Salamander strategy doesn't blindly load layers until Safari
 * jetsam-kills the tab.
 *
 * Bytes are accounted **at register time** based on the buffer's
 * `length × numberOfChannels × 4` (float32) — that's the actual GPU/RAM
 * cost, not the encoded MP3 size on disk.
 *
 * Le seul point d'entrée des buffers est `lib/audio/sample-buffer-cache.ts` :
 * c'est là qu'ils sont enregistrés, une fois par URL décodée, puisque le
 * cache les partage entre tous les synthés.
 */
let totalBytes = 0;

/**
 * Ce qu'il faut d'un buffer pour le peser. `AudioBuffer` et `ToneAudioBuffer`
 * l'exposent tous deux ; on ne demande pas plus pour que les tests puissent
 * passer une façade.
 */
export interface AudioBufferLike {
  readonly length: number;
  readonly numberOfChannels: number;
}

export function bytesForAudioBuffer(buffer: AudioBufferLike): number {
  const bytes = buffer.length * buffer.numberOfChannels * 4;

  // Une façade sans `length` donnerait `NaN`, qui contaminerait le total pour
  // toute la durée du processus.
  if (!Number.isFinite(bytes)) {
    return 0;
  }

  return bytes;
}

export function registerAudioBuffers(buffers: readonly AudioBufferLike[]): void {
  for (const buffer of buffers) {
    totalBytes += bytesForAudioBuffer(buffer);
  }
}

export function unregisterAudioBuffers(buffers: readonly AudioBufferLike[]): void {
  for (const buffer of buffers) {
    totalBytes -= bytesForAudioBuffer(buffer);
  }

  if (totalBytes < 0) {
    totalBytes = 0;
  }
}

export function getAudioBudgetUsedMB(): number {
  return totalBytes / (1024 * 1024);
}

/** Returns `true` if registering an additional `extraMB` would exceed `budgetMB`. */
export function wouldExceedBudget(extraMB: number, budgetMB: number): boolean {
  return getAudioBudgetUsedMB() + extraMB > budgetMB;
}

/** @internal Remet le compteur à zéro entre deux tests. */
export function resetAudioBudgetForTests(): void {
  totalBytes = 0;
}
